import {ApDynformsConfigFieldset}                 from '../../../ap-dynforms/config/ap-dynforms-config-fieldset';
import {ApDynGridColumnConfigBase}                from '../../../ap-dyngrids/config/ap-dyn-grid-column-config-base';
import {ApDynGridPropertyColumnConfig}            from '../../../ap-dyngrids/config/ap-dyn-grid-property-column-config';
import {ApDynformsConfigNumerictextbox}           from '../../../ap-dynforms/config/ap-dynforms-config-numerictextbox';
import {delay, distinctUntilChanged, filter, map} from 'rxjs/operators';
import {ApDynformsValidator}                      from '../../../ap-dynforms/ap-dynforms-validator';
import {Validators}                               from '@angular/forms';
import {Observable}                               from 'rxjs';
import {Injectable, TemplateRef}                  from '@angular/core';
import {ApDynGridGroupColumnConfig}               from '../../../ap-dyngrids/config/ap-dyn-grid-group-column-config';
import {GetRoundNumericPipe}                      from '../../../ap-utils';
import {NAppSettingsConfigBase}                   from './base/n-app-settings-config.base';
import IApplicationSettings = Data.Agronomic.IApplicationSettings;

@Injectable()
export class TargetRateConfig extends NAppSettingsConfigBase {
  private _minAllowed = 0;
  private _maxAllowed = 250;

  constructor(private roundNumericPipe: GetRoundNumericPipe) {
    super();
  }

  public formBuilder(values: any, formValues$: Observable<any>, applicationSettings: IApplicationSettings,
                     isModalField: boolean, applyBlinkEffectOnInit: boolean,
                     diagramTemplateRef?: TemplateRef<any>): ApDynformsConfigFieldset[] {
    const applyBlinkEffect = !(isModalField || !applyBlinkEffectOnInit);
    const parameters = applicationSettings.Parameter;
    return [
      new ApDynformsConfigFieldset({
        key: 'Agronomy',
        legend: 'Global__Agronomy',
        before: diagramTemplateRef,
        useMaxWidth: isModalField,
        config: [
          new ApDynformsConfigNumerictextbox({
            key: 'Threshold',
            label: 'Global__Threshold_Percent',
            value: values['Threshold'],
            min: 0,
            max: 100,
            step: 1,
            decimals: 0,
            format: 'n0',
            blinkOnInit: applyBlinkEffect,
            validators: [
              new ApDynformsValidator({validator: Validators.required, errorKey: 'Settings__Msg_Vali_Value_Required'})
            ]
          }),
          this.generateSlopeControls(parameters, values, applyBlinkEffect)
        ]
      }),
      new ApDynformsConfigFieldset({
        legend: 'Nutrients_Pages_Popups__Amount',
        key: 'Amount',
        useMaxWidth: isModalField,
        config: [
          new ApDynformsConfigNumerictextbox({
            key: 'TargetRate',
            label: 'Fertilisation__TargetRate_Nutrient_kg_N_per_ha',
            value: values['TargetRate'],
            min: formValues$.pipe(
              map((v) => v['ControlRange']),
              distinctUntilChanged((prev, next) => prev === next),
              map((range) => range ? Math.max(0, range) : 0),
              delay(0)
            ),
            max: this._maxAllowed,
            blinkOnInit: applyBlinkEffect,
            validators: [
              new ApDynformsValidator({validator: Validators.required, errorKey: 'Settings__Msg_Vali_Value_Required'})
            ]
          }),
          new ApDynformsConfigNumerictextbox({
            key: 'ControlRange',
            label: 'Global__Control_Range_kg_N_per_ha',
            value: values['ControlRange'],
            min: this._minAllowed,
            max: formValues$.pipe(
              filter(v => v['TargetRate'] && v['TargetRate'] >= 0),
              map(v => v['TargetRate'])
            ),
            blinkOnInit: applyBlinkEffect,
            validators: [
              new ApDynformsValidator({validator: Validators.required, errorKey: 'Settings__Msg_Vali_Value_Required'})
            ]
          }),
          new ApDynformsConfigNumerictextbox({
            key: 'Minimum',
            label: 'Nutrients__Minimum_kg_n_per_ha',
            value: values['Minimum'],
            value$: formValues$.pipe(
              filter((v) => !isNaN(v['TargetRate']) && !isNaN(v['ControlRange'])),
              map((v) => {
                return +v['TargetRate'] - +v['ControlRange'];
              })
            ),
            disabled: true,
            min: this._minAllowed,
            step: 1,
            format: 'n0',
            blinkOnInit: applyBlinkEffect
          }),
          new ApDynformsConfigNumerictextbox({
            key: 'Maximum',
            label: 'Nutrients__Maximum_kg_n_per_ha',
            value: values['Maximum'],
            value$: formValues$.pipe(
              filter((v) => !isNaN(v['TargetRate']) && !isNaN(v['ControlRange'])),
              map((v) => +v['TargetRate'] + +v['ControlRange'])
            ),
            disabled: true,
            min: this._minAllowed,
            step: 1,
            format: 'n0',
            blinkOnInit: applyBlinkEffect
          }),
          new ApDynformsConfigNumerictextbox({
            key: 'Constant',
            label: 'Nutrients__Constant_Amount_kg_N_per_ha',
            value: values['TargetRate'],
            value$: formValues$.pipe(map(v => v['TargetRate'])),
            disabled: true,
            min: formValues$.pipe(
              filter(x => x['Minimum'] != null && x['Minimum'] >= 0),
              map(v => v['Minimum'])
            ),
            max: formValues$.pipe(
              filter(x => x['Maximum'] != null && x['Maximum'] >= 0),
              map(v => v['Maximum'])
            ),
            step: 1,
            format: 'n0',
            blinkOnInit: applyBlinkEffect,
            validators: [
              new ApDynformsValidator({validator: Validators.required, errorKey: 'Settings__Msg_Vali_Value_Required'})
            ]
          })
        ]
      })
    ];
  }

  public gridBuilderConfig(): ApDynGridColumnConfigBase[] {
    return [
      new ApDynGridPropertyColumnConfig({
        title: 'Global__Control_Function',
        field: 'ControlFactorDescription',
        hide: true,
        width: 90
      }),
      new ApDynGridGroupColumnConfig({
        title: 'Global__KgNPerHa',
        groupColumns: [
          new ApDynGridPropertyColumnConfig({
            title: 'AgriConnect_View__TargetRate',
            field: 'TargetRate',
            width: 50
          }),
          new ApDynGridPropertyColumnConfig({
            title: 'Global__Threshold',
            field: 'Threshold',
            hide: true,
            width: 50
          }),
          new ApDynGridPropertyColumnConfig({
            title: 'Global__RateConst',
            field: 'Constant',
            hide: true,
            width: 50,
            pipes: [{
              pipe: this.roundNumericPipe,
              args: [0]
            }]
          }),
          new ApDynGridPropertyColumnConfig({
            title: 'Global__Min',
            field: 'Minimum',
            width: 50,
            pipes: [{
              pipe: this.roundNumericPipe,
              args: [0]
            }]
          }),
          new ApDynGridPropertyColumnConfig({
            title: 'Global__Max',
            field: 'Maximum',
            width: 50,
            pipes: [{
              pipe: this.roundNumericPipe,
              args: [0]
            }]
          })
        ]
      })
    ];
  }

  public updateFormValuesByParameters(values: any, applicationSettings: IApplicationSettings | undefined): void {
    const parameters = applicationSettings.Parameter;
    if (!Number.isFinite(values['Threshold'])) {
      values['Threshold'] = 20;
    }
    if (!Number.isFinite(values['DeadBioMass']) && Number.isFinite(parameters?.deadBioMass)) {
      values['DeadBioMass'] = parameters.deadBioMass;
    } else {
      values['DeadBioMass'] = 0;
    }
    if (!Number.isFinite(values['TargetRate'])) {
      values['TargetRate'] = parameters?.targetValue ?? parameters.recommendation;
    }
    if (!Number.isFinite(values['ControlRange'])) {
      values['ControlRange'] = parameters?.targetValue ?? parameters.recommendation;
    }
    if (!Number.isFinite(values['Constant'])) {
      values['Constant'] = parameters?.uniform;
    }
    if (!Number.isFinite(values['Minimum'])) {
      values['Minimum'] = parameters?.min ?? this._minAllowed;
    }
    if (!Number.isFinite(values['Maximum'])) {
      values['Maximum'] = parameters?.max ?? this._maxAllowed;
    }
    if (!Number.isFinite(values['ControlFactor'])) {
      values['ControlFactor'] = parameters?.slope?.FirstOrDefault()?.Slope ?? 1;
    }
  }
}
